import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useToast } from '@/hooks/use-toast'
import { Gift } from 'lucide-react'
import { useScrollAnimation } from '@/hooks/use-scroll-animation'

const signupSchema = z.object({
  name: z.string().trim().min(3, 'Nome é obrigatório').max(100),
  email: z.string().trim().email('Email inválido').max(255),
  whatsapp: z.string().trim().min(10, 'WhatsApp inválido').max(20),
})

type SignupFormData = z.infer<typeof signupSchema>

const NewsletterSignup = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { toast } = useToast()
  const { ref, isVisible } = useScrollAnimation()

  const form = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: '',
      email: '',
      whatsapp: '',
    },
  })

  const onSubmit = async (data: SignupFormData) => {
    setIsSubmitting(true)

    // Simulação de envio
    setTimeout(() => {
      toast({
        title: 'Cadastro realizado com sucesso!',
        description:
          'Você ganhou 10% OFF na sua primeira compra! Enviamos o cupom no seu email.',
      })

      form.reset()
      setIsSubmitting(false)
    }, 1500)
  }

  return (
    <section className="py-16 bg-muted/30">
      <div className="container mx-auto px-4">
        <div
          ref={ref}
          className={`bg-primary rounded-2xl shadow-sm border border-primary/20 p-6 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="flex flex-col lg:flex-row items-center gap-6">
            <div className="flex items-center gap-4 lg:w-1/3">
              <div className="flex items-center justify-center w-10 h-10 bg-white/20 rounded-full flex-shrink-0">
                <Gift className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <h2 className="font-serif text-xl md:text-2xl font-semibold mb-1 text-white tracking-tight">
                  Ganhe <span className="text-gold font-bold">10% OFF</span> no
                  seu primeiro Fotolivro
                </h2>
              </div>
            </div>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="flex-1 lg:w-2/3"
              >
                <div className="flex flex-col md:flex-row gap-3 items-end">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel className="text-xs text-white/80">
                          Nome
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Seu nome"
                            {...field}
                            className="bg-white h-9 text-sm"
                          />
                        </FormControl>
                        <FormMessage className="text-white/90 text-xs" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel className="text-xs text-white/80">
                          Email
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="seu@email.com"
                            {...field}
                            className="bg-white h-9 text-sm"
                          />
                        </FormControl>
                        <FormMessage className="text-white/90 text-xs" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="whatsapp"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel className="text-xs text-white/80">
                          WhatsApp
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="(11) 98765-4321"
                            {...field}
                            className="bg-white h-9 text-sm"
                          />
                        </FormControl>
                        <FormMessage className="text-white/90 text-xs" />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    className="md:w-auto w-full bg-gold text-white hover:bg-gold/90 h-9 text-sm font-medium"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Cadastrando...' : 'Quero Desconto!'}
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </section>
  )
}

export default NewsletterSignup
